import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle2, MessageSquare, XCircle, User, Shield } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { toast } from "@/hooks/use-toast";

/**
 * AppealThreadDialog — single dialog that renders the full appeal conversation:
 *  1) The original rejection reason (admin/mod side)
 *  2) The clipper's appeal description
 *  3) The admin/mod response (if resolved)
 *
 * When `adminMode` is true and appeal.status === "pending", the dialog also
 * exposes Approve / Deny actions with a response note.
 */
export function AppealThreadDialog({
  open,
  onOpenChange,
  submission,
  appeal,
  adminMode = false,
  onResolved,
}) {
  const [response, setResponse] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (open) {
      setResponse(appeal?.admin_response || "");
      setError(null);
    }
  }, [open, appeal?.admin_response]);

  if (!appeal && !submission) return null;
  const status = appeal?.status || "pending";
  const rejectionReason = submission?.rejection_reason || appeal?.rejection_reason || appeal?.reason_origin || "No reason recorded.";

  async function resolve(newStatus) {
    if (!appeal?.id) return;
    setBusy(true);
    setError(null);
    try {
      await api.put(`/admin/appeals/${appeal.id}`, { status: newStatus, response });
      toast({ title: newStatus === "approved" ? "Appeal approved" : "Appeal denied" });
      onResolved?.(newStatus);
      onOpenChange?.(false);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="appeal-thread-dialog" className="max-w-lg border-[#1a1a1a] bg-[#0a0a0a] text-foreground">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <MessageSquare size={16} className="text-[#c8ff00]" />
            Appeal thread
            <Badge
              data-testid="appeal-thread-status"
              variant="outline"
              className={`ml-1 capitalize ${
                status === "approved"
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                  : status === "denied"
                  ? "border-rose-500/30 bg-rose-500/10 text-rose-300"
                  : "border-amber-500/30 bg-amber-500/10 text-amber-300"
              }`}
            >
              {status}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            {adminMode
              ? "Review the rejection reason and clipper appeal below, then approve or deny."
              : "Here is the full back-and-forth between you and the moderation team."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Original rejection reason */}
          <div data-testid="appeal-thread-rejection" className="rounded-2xl border border-rose-500/20 bg-rose-500/[0.06] p-3 text-xs">
            <div className="mb-1 flex items-center gap-1.5 font-semibold uppercase tracking-wide text-rose-300">
              <AlertCircle size={12} /> Original rejection
            </div>
            <div className="whitespace-pre-wrap break-words text-foreground/90">{rejectionReason}</div>
          </div>

          {/* Clipper's appeal */}
          <div data-testid="appeal-thread-clipper-message" className="rounded-2xl border border-[#1a1a1a] bg-black/40 p-3 text-xs">
            <div className="mb-1 flex items-center gap-1.5 font-semibold uppercase tracking-wide text-[#c8ff00]">
              <User size={12} /> Clipper&apos;s appeal
            </div>
            <div className="whitespace-pre-wrap break-words text-foreground/90">{appeal?.reason || "(no appeal text)"}</div>
            {appeal?.created_at && (
              <div className="mt-1 text-[10px] text-zinc-500">{new Date(appeal.created_at).toLocaleString()}</div>
            )}
          </div>

          {/* Admin/Mod response (if any) */}
          {(appeal?.admin_response || status !== "pending") && (
            <div data-testid="appeal-thread-admin-message" className="rounded-2xl border border-cyan-500/20 bg-cyan-500/[0.06] p-3 text-xs">
              <div className="mb-1 flex items-center gap-1.5 font-semibold uppercase tracking-wide text-cyan-300">
                <Shield size={12} /> Mod/Admin response
              </div>
              <div className="whitespace-pre-wrap break-words text-foreground/90">
                {appeal?.admin_response || (status === "pending" ? "(awaiting response)" : "(no note)")}
              </div>
              {appeal?.updated_at && status !== "pending" && (
                <div className="mt-1 text-[10px] text-zinc-500">{new Date(appeal.updated_at).toLocaleString()}</div>
              )}
            </div>
          )}

          {/* Admin action area */}
          {adminMode && status === "pending" && (
            <div className="space-y-2 pt-1">
              <label className="text-[11px] font-semibold text-zinc-400">Add an optional response note</label>
              <Textarea
                data-testid="appeal-thread-response-input"
                placeholder="Explain your decision (visible to the clipper)..."
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                className="min-h-[80px] border-[#1a1a1a] bg-black text-white"
              />
              {error && (
                <div data-testid="appeal-thread-error" className="rounded-xl border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">
                  {error}
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  data-testid="appeal-thread-approve-btn"
                  className="flex-1 rounded-full bg-emerald-500 font-bold text-black hover:bg-emerald-400"
                  disabled={busy}
                  onClick={() => resolve("approved")}
                >
                  <CheckCircle2 size={14} /> Approve & reinstate clip
                </Button>
                <Button
                  data-testid="appeal-thread-deny-btn"
                  variant="outline"
                  className="flex-1 rounded-full border-rose-500/40 font-bold text-rose-300 hover:bg-rose-500/10"
                  disabled={busy}
                  onClick={() => resolve("denied")}
                >
                  <XCircle size={14} /> Deny appeal
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
